import { Asset } from '@/platform/asset';
import * as FileSystem from '@/platform/file-system';
import { NativeModules, TurboModuleRegistry } from '@/ui/rn';

/**
 * ArcFace (InsightFace `w600k_mbf`, 512-d) via ONNX Runtime.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POR QUE ESTE MOTOR, E NÃO O MOBILEFACENET
 * ─────────────────────────────────────────────────────────────────────────────
 * Não é só acurácia: é **o mesmo espaço de embedding** do coletor TirvuTab e do
 * tab web. Com os três no mesmo espaço, a biometria cadastrada aqui serve lá e
 * vice-versa — o colaborador cadastra uma vez, no celular, e bate ponto no tablet
 * do posto sem cadastrar de novo.
 *
 * O arquivo é byte a byte o `buffalo_s` oficial do InsightFace
 * (sha256 `9cc6e4a7…19eb4f`), o mesmo que o web já serve.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * O QUE ESTE ARQUIVO **NÃO** FAZ
 * ─────────────────────────────────────────────────────────────────────────────
 * O alinhamento. Aqui ele já chega pronto: o `modules/tirvu-face` recorta e alinha
 * em NATIVO (ML Kit no Android, Vision no iOS), com a orientação EXIF aplicada uma
 * única vez antes de detectar. É uma vantagem real sobre o TirvuTab, que faz o
 * alinhamento em JS — e onde justamente por isso apareceram, em aparelho, um roll
 * em 0..360 e landmarks com outro nome de campo.
 *
 * O que chega aqui é o tile 112×112 já canônico. Só falta normalizar e inferir.
 */

/** Lado do quadro canônico do ArcFace. */
export const TAMANHO_ARCFACE = 112;

/** Dimensão do embedding. */
export const DIMENSAO_ARCFACE = 512;

/**
 * Versão do descritor, no formato `"v10:f1,f2,…"`.
 *
 * ⚠️ Tem que ser **10**, igual ao tab web e ao TirvuTab — é a chave que diz se dois
 * descritores são comparáveis. O `v` inicial é obrigatório: o parser do web faz
 * `slice(1, ':')`, então sem ele o primeiro dígito é comido.
 */
export const VERSAO_DESCRITOR_ARCFACE = 10;

/**
 * Provedores de execução, em ordem de preferência.
 *
 * `xnnpack` primeiro (CPU otimizada, bem mais rápida); `cpu` puro como reserva. Não
 * há NNAPI/CoreML aqui de propósito: em aparelho intermediário eles ora não
 * suportam algum operador do grafo, ora caem para CPU por trás, e o ganho não paga
 * o risco de falhar o carregamento inteiro.
 */
const PROVEDORES: string[][] = [['xnnpack', 'cpu'], ['cpu']];

/** Onde o modelo é materializado, para o ORT abrir por CAMINHO e não por URI. */
const NOME_ARQUIVO = 'w600k_mbf.onnx';

const documentos = (): string => (FileSystem as any).documentDirectory ?? '';

/**
 * O ORT nativo abre por CAMINHO DE ARQUIVO, não por URI. `documentDirectory` vem
 * como `file:///...` — passar isso direto faz o carregamento falhar com um erro
 * que não diz nada sobre o motivo.
 */
const semEsquema = (uri: string): string => uri.replace(/^file:\/\//, '');

/**
 * O módulo nativo do ONNX Runtime está registrado neste binário?
 *
 * ⚠️ Perguntar ANTES de importar não é preciosismo. O `onnxruntime-react-native`
 * faz, no escopo do módulo:
 *
 *     const Module = NativeModules.Onnxruntime;
 *     if (typeof globalThis.OrtApi === 'undefined') Module.install();
 *
 * Sem o nativo, `Module` é `undefined` e isso vira `TypeError: Cannot read property
 * 'install' of undefined` **durante a avaliação do módulo** — antes de qualquer
 * try/catch em volta do `await import()` conseguir pegar. No Expo Go, onde o nativo
 * não existe, o app morreria na tela vermelha em vez de degradar.
 *
 * Checa os dois registros porque, sob a New Architecture, `NativeModules` é um proxy
 * do TurboModuleRegistry e um módulo pode aparecer num e não no outro.
 */
const temModuloNativo = (nome: string): boolean => {
  try {
    if ((NativeModules as any)?.[nome] != null) return true;
  } catch {
    /* o proxy pode lançar quando o registro nem existe */
  }
  try {
    return TurboModuleRegistry.get(nome) != null;
  } catch {
    return false;
  }
};

let promessaSessao: Promise<any | null> | null = null;
let nomeEntrada = '';
let nomeSaida = '';

/**
 * Copia o modelo embarcado para um caminho estável.
 *
 * O asset vem do bundle (Android: `res/`, iOS: dentro do .app) e nem sempre é um
 * caminho de arquivo que o nativo consiga abrir. Materializar uma vez em
 * `documentDirectory` resolve, e nas aberturas seguintes o arquivo já está lá.
 */
const materializarModelo = async (): Promise<string | null> => {
  const destino = `${documentos()}modelos/${NOME_ARQUIVO}`;

  try {
    const info = await FileSystem.getInfoAsync(destino);
    // Confere o TAMANHO, não só a existência: uma cópia interrompida (app morto no
    // meio, disco cheio) deixa um arquivo truncado que o ORT rejeita com um erro
    // genérico, e sem esta checagem ele seria reaproveitado para sempre.
    if (info.exists && (info as any).size > 1_000_000) return semEsquema(destino);
  } catch {
    /* segue e recopia */
  }

  try {
    await FileSystem.makeDirectoryAsync(`${documentos()}modelos`, {
      intermediates: true,
    });
  } catch {
    /* já existe */
  }

  try {
    const asset = Asset.fromModule("/models/w600k_mbf.onnx" /* WEB: modelo servido pelo PWA */);
    await asset.downloadAsync();
    const origem = asset.localUri ?? asset.uri;
    if (!origem) return null;

    await FileSystem.copyAsync({ from: origem, to: destino });
    return semEsquema(destino);
  } catch (e) {
    console.warn('[ArcFace] Falha ao materializar o modelo:', e);
    return null;
  }
};

/** Abre (uma vez) a sessão do ORT. `null` quando o motor não está disponível. */
const obterSessao = async (): Promise<any | null> => {
  if (promessaSessao) return promessaSessao;

  promessaSessao = (async () => {
    // WEB: o motor é o onnxruntime-web, que está SEMPRE no bundle (src/ml/onnxruntime.ts).
    // A checagem do módulo NATIVO (proteção do Expo Go) não se aplica aqui: no navegador
    // `NativeModules` é vazio, e com ela o reconhecimento ficava indisponível em TODO
    // navegador — "Erro" ao reconhecer o rosto, no iPhone e no computador.
    void temModuloNativo;

    const caminho = await materializarModelo();
    if (!caminho) return null;

    const ort = await import('@/ml/onnxruntime');

    for (const executionProviders of PROVEDORES) {
      try {
        const s = await ort.InferenceSession.create(caminho, {
          executionProviders,
          // Dois threads: o suficiente para o ganho, sem disputar o processador com
          // a câmera e a interface no aparelho de entrada.
          intraOpNumThreads: 2,
        } as any);

        nomeEntrada = s.inputNames?.[0] ?? '';
        nomeSaida = s.outputNames?.[0] ?? '';

        if (!nomeEntrada || !nomeSaida) {
          console.warn('[ArcFace] Sessão abriu sem nomes de entrada/saída.');
          return null;
        }

        console.log(
          `[ArcFace] Sessão aberta com [${executionProviders.join(', ')}] · ` +
            `entrada "${nomeEntrada}" · saída "${nomeSaida}".`,
        );
        return s;
      } catch (e) {
        console.warn(
          `[ArcFace] Falhou com [${executionProviders.join(', ')}]:`,
          e,
        );
      }
    }

    return null;
  })();

  return promessaSessao;
};

/**
 * Embedding de 512 dimensões, L2-normalizado, a partir do tile 112×112 alinhado.
 *
 * ⚠️ O tile do `tirvu-face` é **RGB**, 3 bytes por pixel — não RGBA. Ler como se
 * fossem 4 desalinha todos os canais e o resultado não é um embedding pior: é o
 * embedding de outra imagem, sem erro nenhum para avisar.
 *
 * ⚠️ E a diferença para o mobilefacenet NÃO é a normalização — os dois usam
 * `(v − 127,5) / 127,5`. É o **layout**: o mobilefacenet consome NHWC (o tile como
 * veio, intercalado) e o ArcFace consome **NCHW** (os três canais em planos
 * separados). Errar isso também não dá erro, dá lixo.
 */
export const descritorDeTile = async (
  tileRgb: Uint8Array,
  lado = TAMANHO_ARCFACE,
): Promise<Float32Array | null> => {
  const sessao = await obterSessao();
  if (!sessao) return null;

  const pixels = lado * lado;
  if (tileRgb.length !== pixels * 3) {
    console.warn(
      `[ArcFace] Tile com ${tileRgb.length} bytes, esperado ${pixels * 3} (RGB ${lado}×${lado}).`,
    );
    return null;
  }

  // Intercalado (RGBRGB…) → planar (RRR…GGG…BBB…).
  const tensor = new Float32Array(3 * pixels);
  for (let i = 0; i < pixels; i++) {
    const base = i * 3;
    tensor[i] = (tileRgb[base] - 127.5) / 127.5;
    tensor[pixels + i] = (tileRgb[base + 1] - 127.5) / 127.5;
    tensor[2 * pixels + i] = (tileRgb[base + 2] - 127.5) / 127.5;
  }

  try {
    const ort = await import('@/ml/onnxruntime');
    const entrada = new ort.Tensor('float32', tensor, [1, 3, lado, lado]);
    const saida = await sessao.run({ [nomeEntrada]: entrada });
    const bruto = saida[nomeSaida]?.data as Float32Array | undefined;

    if (!bruto || bruto.length !== DIMENSAO_ARCFACE) {
      console.warn(
        `[ArcFace] Saída inesperada (${bruto?.length} ≠ ${DIMENSAO_ARCFACE}).`,
      );
      return null;
    }

    // L2. Sem isto a distância não tem escala fixa e nenhum limiar vale.
    let norma = 0;
    for (let i = 0; i < bruto.length; i++) norma += bruto[i] * bruto[i];
    norma = Math.sqrt(norma) || 1;

    const normalizado = new Float32Array(DIMENSAO_ARCFACE);
    for (let i = 0; i < bruto.length; i++) normalizado[i] = bruto[i] / norma;

    return normalizado;
  } catch (e) {
    console.warn('[ArcFace] Falha na inferência:', e);
    return null;
  }
};

/** O motor está de pé neste aparelho? Usado para decidir se degrada. */
export const arcFaceDisponivel = async (): Promise<boolean> =>
  (await obterSessao()) != null;

/**
 * Serializa no formato do banco: `"v10:f1,f2,…"`.
 *
 * ⚠️ Formato COMPARTILHADO com o tab web e o TirvuTab. Mudar aqui quebra a
 * interoperabilidade que é o motivo de existir deste motor.
 */
export const serializarDescritor = (v: Float32Array): string =>
  `v${VERSAO_DESCRITOR_ARCFACE}:${Array.from(v)
    .map((n) => n.toFixed(6))
    .join(',')}`;

/** Lê `"v10:…"`. Devolve `null` para outra versão — espaços não se misturam. */
export const desserializarDescritor = (texto?: string | null): Float32Array | null => {
  const s = String(texto ?? '');
  const sep = s.indexOf(':');
  if (sep < 1) return null;
  if (Number(s.slice(1, sep)) !== VERSAO_DESCRITOR_ARCFACE) return null;

  const partes = s.slice(sep + 1).split(',').map(Number);
  return partes.length === DIMENSAO_ARCFACE && partes.every(Number.isFinite)
    ? Float32Array.from(partes)
    : null;
};

export const FaceArcFace = {
  descritorDeTile,
  arcFaceDisponivel,
  serializarDescritor,
  desserializarDescritor,
  TAMANHO_ARCFACE,
  DIMENSAO_ARCFACE,
  VERSAO_DESCRITOR_ARCFACE,
};
